import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Shared workspace list for LearnLayout sidebars (desktop + mobile).
 * Order: created_at descending, limit 20 — never re-sorted on open.
 */
const useSidebarWorkspaces = (userId, workspaceId, workspaceName) => {
  const [workspaces, setWorkspaces] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchWorkspaces = async () => {
      if (!userId) {
        setWorkspaces([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('workspaces')
          .select('id, name, created_at')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(20);

        if (error) throw error;
        if (!cancelled) setWorkspaces(data || []);
      } catch (fetchError) {
        console.error('Error fetching workspaces for sidebar:', fetchError);
        if (!cancelled) setWorkspaces([]);
      }
    };

    fetchWorkspaces();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // After "+ New workspace", the new row isn't in the stable list yet — prepend it.
  useEffect(() => {
    if (!workspaceId || !workspaceName) return;
    setWorkspaces((prev) => {
      if (prev.some((workspace) => workspace.id === workspaceId)) return prev;
      return [
        { id: workspaceId, name: workspaceName, created_at: new Date().toISOString() },
        ...prev,
      ];
    });
  }, [workspaceId, workspaceName]);

  const renameInList = useCallback((workspaceIdToRename, name) => {
    setWorkspaces((prev) =>
      prev.map((workspace) =>
        workspace.id === workspaceIdToRename
          ? { ...workspace, name }
          : workspace
      )
    );
  }, []);

  const removeFromList = useCallback((workspaceIdToRemove) => {
    setWorkspaces((prev) =>
      prev.filter((workspace) => workspace.id !== workspaceIdToRemove)
    );
  }, []);

  const addToList = useCallback((workspace) => {
    setWorkspaces((prev) => {
      if (prev.some((item) => item.id === workspace.id)) return prev;
      return [workspace, ...prev];
    });
  }, []);

  return {
    workspaces,
    renameInList,
    removeFromList,
    addToList,
  };
};

export default useSidebarWorkspaces;
